import type {
  KgInsight,
  KgInsightDetailsGridSection,
  KgInsightHeroSection,
  KgInsightSentiment,
  KgInsightTakeawaySection,
} from '@api/api/kg-insights';
import type {
  KeyStatItem,
  ValueSegment,
} from '@design/components/key-stats/KeyStats';
import { config } from '@terminal/config';
import { appPaths } from '@terminal/routes/app-paths';
import type { TopTakeawaysRow } from '../../top-takeaways/TopTakeaways';

export const findSection = <T extends KgInsight['sections'][number]>(
  sections: KgInsight['sections'],
  type: T['type']
) => sections.find(s => s.type === type) as T | undefined;

export const heroToKeyStats = (hero: KgInsightHeroSection): KeyStatItem[] =>
  hero.data.badges.map(badge => ({
    label: badge.label,
    value: [{ text: badge.value }],
  }));

export const detailsGridToKeyStats = (
  section: KgInsightDetailsGridSection
): KeyStatItem[] => {
  const mapSentimentToColorVariant: Record<
    KgInsightSentiment,
    ValueSegment['colorVariant']
  > = {
    positive: 'bullish',
    negative: 'bearish',
    neutral: undefined,
  };

  return section.data.cards.map(card => {
    const colorVariant = card.sentiment
      ? mapSentimentToColorVariant[card.sentiment]
      : undefined;
    const value: ValueSegment[] = card.value_details
      ? [
          { text: card.value },
          {
            text: card.value_details,
            colorVariant,
          },
        ]
      : [{ text: card.value, colorVariant }];

    return {
      label: card.label,
      value,
    };
  });
};

export const takeawayToRows = (
  takeaway: KgInsightTakeawaySection
): TopTakeawaysRow[] =>
  takeaway.data.cards.map(card => {
    const themes = card.tags.map(tag => {
      const themeLink = tag.id
        ? `${config.urls.webAppOrigin}${appPaths.themeOverview}/${tag.id}`
        : undefined;

      return {
        label: tag.label,
        kind: tag.kind,
        link: themeLink,
        themeId: tag.id,
      };
    });

    return {
      title: card.title,
      description: card.subtitle,
      themes,
    };
  });
